import { createElement, useEffect } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { toastApiRef } from '@backstage/frontend-plugin-api';
import { notificationsApiRef } from '@backstage/plugin-notifications';
import type {
  NotificationSeverity,
  NotificationSignal,
} from '@backstage/plugin-notifications-common';
import { NotificationToastActions } from './NotificationToastActions';

const toastStatus = {
  low: 'info',
  normal: 'success',
  high: 'warning',
  critical: 'danger',
} as const satisfies Record<NotificationSeverity, string>;

export function useNotificationToasts(
  signal: NotificationSignal | null | undefined,
) {
  const notificationsApi = useApi(notificationsApiRef);
  const toastApi = useApi(toastApiRef);

  useEffect(() => {
    if (signal?.action !== 'new_notification') {
      return;
    }

    notificationsApi
      .getNotification(signal.notification_id)
      .then(notification => {
        if (!notification) {
          return;
        }

        const toast = toastApi.post({
          title: notification.payload.title,
          description: createElement(NotificationToastActions, {
            notificationId: notification.id,
            onDismiss: () => toast.close(),
          }),
          status: toastStatus[notification.payload.severity ?? 'normal'],
          timeout: 15000,
        });
      })
      .catch(() => {
        toastApi.post({
          title: 'Failed to fetch notification',
          status: 'danger',
          timeout: 15000,
        });
      });
  }, [signal, notificationsApi, toastApi]);
}
